import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { API_BASE_URL } from '../../../config';
import styles from './DespesasGerais.module.css';
import { gerarRelatorioFrotaPDF } from './geradorRelatorioFrota';
import logo from '../../../img/logo rio verde pdf.png';
import PageHeader from '../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay'; // Importado

// Ícones
import { FiArrowLeft, FiDollarSign, FiTool, FiSearch, FiCalendar, FiFilter, FiTruck, FiPieChart, FiBarChart2, FiTrendingUp, FiPrinter, FiList, FiGrid } from 'react-icons/fi';
import { FaGasPump, FaWrench, FaTools, FaCarCrash, FaFileInvoiceDollar, FaShieldAlt, FaQuestionCircle, FaFilter as FaFilterIcon } from 'react-icons/fa';

// Registro de componentes do Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const ITEMS_PER_PAGE = 15;

// Funções Utilitárias
const formatCurrency = (value) => value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value) : 'R$ 0,00';
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Adiciona verificação para garantir que a data seja válida antes de formatar
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data Inválida';
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const iconMap = {
    'Combustível': <FaGasPump title="Combustível" />,
    'Manutenção Corretiva': <FaWrench title="Manutenção Corretiva" />,
    'Manutenção Preventiva': <FaTools title="Manutenção Preventiva" />,
    'Lubrificantes/Filtros': <FaFilterIcon title="Lubrificantes/Filtros" />,
    'Pneus': <FaCarCrash title="Pneus" />,
    'Documentação': <FaFileInvoiceDollar title="Documentação" />,
    'Seguro': <FaShieldAlt title="Seguro" />,
    'Outros': <FaQuestionCircle title="Outros" />,
};

const DespesasGerais = () => {
    // Estados
    const [despesas, setDespesas] = useState([]);
    const [veiculos, setVeiculos] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Mantido para feedback interno
    const [pageLoading, setPageLoading] = useState(true); // Novo estado para overlay
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterVeiculo, setFilterVeiculo] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [chartType, setChartType] = useState('doughnut');
    const chartContainerRef = useRef(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' ou 'monthly'
    const [selectedMonth, setSelectedMonth] = useState(null);
    const navigate = useNavigate(); // Hook useNavigate

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();
        setPageLoading(true); // Inicia loading do overlay

        const fetchAllData = async () => {
            setIsLoading(true); // Mantido
            try {
                const token = localStorage.getItem('token');
                 if (!token) { // Adiciona verificação de token no início
                    console.error("Token não encontrado, redirecionando para login.");
                    navigate('/'); // Redireciona se não houver token
                    return;
                }
                const headers = { 'Authorization': `Bearer ${token}` };
                const [despesasRes, veiculosRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/despesas/frota`, { headers }),
                    axios.get(`${API_BASE_URL}/api/veiculos`, { headers })
                ]);
                setDespesas(despesasRes.data);
                setVeiculos(veiculosRes.data);
            } catch (err) {
                console.error("Erro ao buscar dados da frota:", err);
                 if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                    // Trata erro de autenticação/autorização
                    setError("Sessão expirada ou inválida. Faça login novamente.");
                    navigate('/'); // Redireciona em caso de erro de autenticação
                } else {
                    setError("Não foi possível carregar os dados. Tente novamente mais tarde.");
                }
            } finally {
                setIsLoading(false); // Mantido
                const endTime = Date.now();
                const duration = endTime - startTime;
                const remainingTime = MIN_LOADING_TIME - duration;

                if (remainingTime > 0) {
                    setTimeout(() => setPageLoading(false), remainingTime);
                } else {
                    setPageLoading(false); // Termina loading do overlay
                }
            }
        };
        fetchAllData();
    }, [navigate]); // Adiciona navigate como dependência

    const filteredDespesas = useMemo(() => {
        let items = despesas;

        if (selectedMonth) {
            items = items.filter(d => {
                try {
                    const itemMonth = new Date(d.data).toLocaleString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
                    return itemMonth.toLowerCase() === selectedMonth.toLowerCase();
                } catch (e) { return false; } // Ignora datas inválidas
            });
        }
        
        return items.filter(d => {
            try {
                const itemDate = new Date(d.data);
                if (isNaN(itemDate.getTime())) return false; // Ignora datas inválidas

                const startDate = filterStartDate ? new Date(filterStartDate) : null;
                const endDate = filterEndDate ? new Date(filterEndDate) : null;
                // Ajuste para incluir o dia final no filtro
                 if (startDate) startDate.setUTCHours(0, 0, 0, 0);
                 if (endDate) endDate.setUTCHours(23, 59, 59, 999);
                
                // Normaliza a data do item para comparar apenas dia/mês/ano
                 const normalizedItemDate = new Date(Date.UTC(itemDate.getUTCFullYear(), itemDate.getUTCMonth(), itemDate.getUTCDate()));

                return (
                    (!startDate || normalizedItemDate >= startDate) &&
                    (!endDate || normalizedItemDate <= endDate) &&
                    (!filterType || d.tipo_despesa === filterType) &&
                    (!filterVeiculo || d.veiculo_id === parseInt(filterVeiculo)) &&
                    (!searchTerm ||
                        (d.descricao && d.descricao.toLowerCase().includes(searchTerm.toLowerCase())) ||
                        (d.fornecedor_nome && d.fornecedor_nome.toLowerCase().includes(searchTerm.toLowerCase())) || // Busca pelo nome do fornecedor
                        (d.placa && d.placa.toLowerCase().includes(searchTerm.toLowerCase())) || // Busca pela placa
                        (d.marca && d.marca.toLowerCase().includes(searchTerm.toLowerCase())) || // Busca pela marca
                        (d.modelo && d.modelo.toLowerCase().includes(searchTerm.toLowerCase())) // Busca pelo modelo
                    )
                );
            } catch (e) {
                console.warn("Erro ao filtrar despesa:", d, e);
                return false; // Ignora itens com erro
            }
        });
    }, [despesas, searchTerm, filterType, filterVeiculo, filterStartDate, filterEndDate, selectedMonth]);


    const monthlySummaryData = useMemo(() => {
        const groups = filteredDespesas.reduce((acc, d) => {
             try {
                const date = new Date(d.data);
                if (isNaN(date.getTime())) throw new Error('Data inválida');
                const month = date.toLocaleString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
                if (!acc[month]) {
                    acc[month] = { total: 0, count: 0 };
                }
                const value = parseFloat(d.valor);
                if (!isNaN(value)) {
                    acc[month].total += value;
                    acc[month].count++;
                }
             } catch(e) { /* Ignora data inválida */ }
            return acc;
        }, {});

        // Ordena por data (mais recente primeiro)
        return Object.entries(groups).sort(([monthA], [monthB]) => {
             try {
                // Converte "janeiro de 2024" para um objeto Date para comparação
                const parseMonthYear = (my) => {
                    const [m, , y] = my.toLowerCase().split(' ');
                    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
                    return new Date(y, months.indexOf(m));
                };
                const dateA = parseMonthYear(monthA);
                const dateB = parseMonthYear(monthB);
                 if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0; // Fallback
                return dateB - dateA; // Ordena do mais recente para o mais antigo
             } catch(e) { return 0; } // Fallback em caso de erro
        });
    }, [filteredDespesas]);


    const paginatedDespesas = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredDespesas.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredDespesas, currentPage]);
    const totalPages = Math.ceil(filteredDespesas.length / ITEMS_PER_PAGE);

    const { chartData, kpiData } = useMemo(() => {
        if (!filteredDespesas || filteredDespesas.length === 0) {
            return { chartData: null, kpiData: { custoTotal: 0, principalDespesa: 'N/A' } };
        }

        const totaisPorTipo = filteredDespesas.reduce((acc, d) => {
             const value = parseFloat(d.valor);
             if (!isNaN(value)) { // Soma apenas se for um número válido
                acc[d.tipo_despesa] = (acc[d.tipo_despesa] || 0) + value;
             }
            return acc;
        }, {});
        
        const sortedData = Object.entries(totaisPorTipo).sort(([, a], [, b]) => b - a);
        const labels = sortedData.map(([tipo]) => tipo);
        const data = sortedData.map(([, total]) => total);
        const softPalette = ['#5B7A9D', '#6AB187', '#FACF5A', '#F78E69', '#A87B9A', '#77C2A7', '#F4A66E', '#D296B6'];
        const newChartData = { labels, datasets: [{ label: 'Total Gasto', data, backgroundColor: softPalette.slice(0, labels.length), borderWidth: 0 }] };
        
        const custoTotal = data.reduce((sum, val) => sum + val, 0);
        const principalDespesa = labels.length > 0 ? labels[0] : 'N/A';

        return { chartData: newChartData, kpiData: { custoTotal, principalDespesa } };
    }, [filteredDespesas]);
    
    const handleGerarRelatorio = () => {
         if (filteredDespesas.length === 0) {
            alert("Não há dados para gerar o relatório com os filtros atuais.");
            return;
        }
        gerarRelatorioFrotaPDF({
            despesas: filteredDespesas, // Usa as despesas já filtradas
            logo: logo,
            chartContainerRef,
            tituloPersonalizado: selectedMonth ? `Relatório de Despesas - ${selectedMonth}` : 'Relatório de Despesas da Frota'
        });
    };

    const handleSelectMonth = (month) => {
        setSelectedMonth(month);
        setViewMode('list');
        setCurrentPage(1);
    };

    const handleBackToSummary = () => {
        setSelectedMonth(null);
        setViewMode('monthly');
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right', labels: { boxWidth: 20, padding: 15, font: { size: 12 } } },
            title: { display: false },
            datalabels: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${formatCurrency(ctx.raw)}` } }
        }
    };
    
    // Não precisa mais renderizar loading aqui, pois pageLoading controla isso
    // if (isLoading) return <div className={styles.loadingText}>Carregando despesas da frota...</div>;
    // if (error && !isLoading) return <div className={styles.errorText}>{error}</div>; // Mostra erro apenas se não estiver carregando

    return (
        <div className={styles.container}>
            {pageLoading && <LoadingOverlay text="Carregando despesas..." />}

            {!pageLoading && (
                <>
                    <div ref={chartContainerRef} style={{ position: 'fixed', left: '-9999px', top: '-9999px' }} />

                    <PageHeader 
                        title="Despesas da Frota"
                        subtitle="Análise e listagem de todas as despesas registradas."
                        backTo="/sistema-frota"
                    />

                    {error && <div className={styles.errorText}>{error}</div>} 

                    {!error && (
                        <>
                            <div className={styles.contentBlock}>
                                <h2 className={styles.sectionTitle}>{selectedMonth ? `Indicadores de ${selectedMonth}` : 'Indicadores Gerais (Filtro)'}</h2>
                                <div className={styles.kpiContainer}>
                                    <div className={styles.kpiCard}>
                                        <div className={styles.kpiIcon}><FiDollarSign /></div>
                                        <div>
                                            <div className={styles.kpiValue}>{formatCurrency(kpiData.custoTotal)}</div>
                                            <div className={styles.kpiLabel}>Custo Total</div>
                                        </div>
                                    </div>
                                    <div className={styles.kpiCard}>
                                        <div className={styles.kpiIcon}><FiTool /></div>
                                        <div>
                                            <div className={styles.kpiValue}>{kpiData.principalDespesa}</div>
                                            <div className={styles.kpiLabel}>Principal Despesa</div>
                                        </div>
                                    </div>
                                </div>
                                {chartData && chartData.labels.length > 0 ? (
                                    <div className={styles.chartContainer}>
                                        <div className={styles.chartHeader}>
                                            <h3 className={styles.chartTitle}>Despesas por Tipo</h3>
                                            <div className={styles.chartToggleContainer}>
                                                <button className={`${styles.chartToggleButton} ${chartType === 'doughnut' ? styles.active : ''}`} onClick={() => setChartType('doughnut')} title="Gráfico de Rosca"><FiPieChart /></button>
                                                <button className={`${styles.chartToggleButton} ${chartType === 'bar' ? styles.active : ''}`} onClick={() => setChartType('bar')} title="Barras Verticais"><FiBarChart2 /></button>
                                            </div>
                                        </div>
                                        <div className={styles.chartWrapper}>
                                            {chartType === 'doughnut' ? <Doughnut data={chartData} options={chartOptions} /> : <Bar data={chartData} options={{ ...chartOptions, indexAxis: 'y', plugins: { ...chartOptions.plugins, legend: { display: false } } }} />}
                                        </div>
                                    </div>
                                ) : (
                                     <p className={styles.noDataChart}>Não há dados suficientes para exibir o gráfico com os filtros aplicados.</p>
                                )}
                            </div>

                            <div className={styles.contentBlock}>
                                <div className={styles.listHeader}>
                                    <h2 className={styles.sectionTitle}>
                                        {viewMode === 'list' && selectedMonth ? `Detalhes de ${selectedMonth}` : 'Listagem de Despesas'}
                                    </h2>
                                    <div className={styles.headerActions}>
                                        <div className={styles.viewModeToggle}>
                                            <button className={viewMode === 'list' ? styles.active : ''} onClick={() => { setViewMode('list'); setSelectedMonth(null); }}><FiList /> Lista</button>
                                            <button className={viewMode === 'monthly' ? styles.active : ''} onClick={() => { setViewMode('monthly'); setSelectedMonth(null); }}><FiGrid /> Mês</button>
                                        </div>
                                        <button className={styles.reportButton} onClick={handleGerarRelatorio} disabled={filteredDespesas.length === 0}>
                                            <FiPrinter /> Gerar Relatório
                                        </button>
                                    </div>
                                </div>
                                
                                {!selectedMonth && viewMode === 'list' && ( // Mostra filtros apenas na visão de lista geral
                                    <div className={styles.filterContainer}>
                                        <div className={styles.filterGroup}><FiSearch /><input type="text" placeholder="Buscar..." className={styles.formInput} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
                                        <div className={styles.filterGroup}><FiFilter /><select className={styles.formSelect} value={filterType} onChange={e => setFilterType(e.target.value)}><option value="">Todos os Tipos</option><option value="Combustível">Combustível</option><option value="Manutenção Corretiva">Manutenção Corretiva</option><option value="Manutenção Preventiva">Manutenção Preventiva</option><option value="Lubrificantes/Filtros">Lubrificantes/Filtros</option><option value="Pneus">Pneus</option><option value="Documentação">Documentação</option><option value="Seguro">Seguro</option><option value="Outros">Outros</option></select></div>
                                        <div className={styles.filterGroup}><FiTruck /><select className={styles.formSelect} value={filterVeiculo} onChange={e => setFilterVeiculo(e.target.value)}><option value="">Todos os Veículos</option>{veiculos.map(v => <option key={v.id} value={v.id}>{`${v.marca} ${v.modelo} (${v.placa || 'N/A'})`}</option>)}</select></div>
                                        <div className={`${styles.filterGroup} ${styles.dateFilterGroup}`}><FiCalendar /><input type="date" className={styles.formInput} value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} /><span className={styles.dateSeparator}>até</span><input type="date" className={styles.formInput} value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} /></div>
                                    </div>
                                )}

                                {viewMode === 'list' ? (
                                    <>
                                        {selectedMonth && <button className={styles.backButton} onClick={handleBackToSummary}><FiArrowLeft /> Voltar para Resumo Mensal</button>}
                                        <div className={styles.tableWrapper}>
                                            <table className={styles.despesasTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Veículo</th>
                                                        <th>Data</th>
                                                        <th>Descrição</th>
                                                        <th>Fornecedor</th>
                                                        <th>Consumo</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedDespesas.length > 0 ? paginatedDespesas.map(d => (
                                                        <tr key={d.id}>
                                                            <td data-label="Veículo">{`${d.marca || '?'} ${d.modelo || '?'} `}<span className={styles.placa}>{d.placa || ''}</span></td>
                                                            <td data-label="Data">{formatDate(d.data)}</td>
                                                            <td data-label="Descrição">
                                                                <div className={styles.descriptionCellContent}>
                                                                    <span className={styles.iconWrapper}>{iconMap[d.tipo_despesa] || iconMap['Outros']}</span>
                                                                    <span className={styles.truncate} title={d.descricao}>{d.descricao}</span>
                                                                </div>
                                                            </td>
                                                            <td data-label="Fornecedor">{d.fornecedor_nome || 'N/A'}</td>
                                                            <td data-label="Consumo">
                                                                {d.tipo_despesa === 'Combustível' && d.consumo_km_l ? 
                                                                    `${parseFloat(d.consumo_km_l).toFixed(2)} KM/L` : 'N/A'}
                                                            </td>
                                                            <td data-label="Total" className={styles.valor}>{formatCurrency(d.valor)}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr><td colSpan="6" className={styles.noData}>Nenhuma despesa encontrada para os filtros aplicados{selectedMonth ? ` em ${selectedMonth}` : ''}.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {totalPages > 1 && (
                                            <div className={styles.paginationContainer}>
                                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</button>
                                                <span>Página {currentPage} de {totalPages}</span>
                                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Próxima</button>
                                            </div>
                                        )}
                                    </>
                                ) : ( // viewMode === 'monthly'
                                    <div className={styles.monthlyGrid}>
                                         {monthlySummaryData.length > 0 ? monthlySummaryData.map(([month, data]) => (
                                            <div key={month} className={styles.monthCard} onClick={() => handleSelectMonth(month)}>
                                                <span className={styles.monthCardTitle}>{month}</span>
                                                <span className={styles.monthCardTotal}>{formatCurrency(data.total)}</span>
                                                <span className={styles.monthCardCount}>{data.count} {data.count !== 1 ? 'lançamentos' : 'lançamento'}</span>
                                            </div>
                                        )) : (
                                             <p className={styles.noDataMonth}>Nenhum mês com despesas encontrado para os filtros aplicados.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default DespesasGerais;